import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, Flag, LineChart, Pencil, Plus, Target, Trash2, Trophy, X } from "lucide-react";
import { Card, Eyebrow, monthLabel, MonthDateInput, usDate } from "./ui";

const TYPES = [["cash_buffer", "Cash buffer"], ["debt_free", "Debt-free by a date"], ["debt_paydown", "Pay down a specific debt"]];
const STATE = {
  on_track: ["On track", "#059669", "#ECFDF5"],
  behind: ["Behind", "#DC2626", "#FEF2F2"],
  achieved: ["Achieved", "#7C3AED", "#F5F3FF"],
};
const bc = { borderColor: "var(--ll-border)" };
const inp = "border px-2 py-1.5 text-sm bg-white";
const EMPTY = { type: "cash_buffer", name: "", target_amount: "", target_month: "", debt_id: "" };

export default function GoalsPanel({ title = "Your goals", testid = "goals-panel", onPlanned }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/advisory/goals")).data); } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const errMsg = (err) => {
    const d = err?.response?.data?.detail;
    return Array.isArray(d) ? d[0]?.msg : d || "Save failed";
  };
  const save = async () => {
    const body = {
      name: form.name,
      target_amount: form.type === "cash_buffer" ? Number(form.target_amount) || null : null,
      target_month: form.target_month || null,
    };
    try {
      if (editId) await api.patch(`/advisory/goals/${editId}`, body);
      else await api.post("/advisory/goals", { ...body, type: form.type, debt_id: form.debt_id || null });
      toast.success(editId ? "Goal updated" : "Goal added");
      setForm(null); setEditId(null);
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (g) => {
    if (!window.confirm(`Delete goal "${g.name}"?`)) return;
    try { await api.delete(`/advisory/goals/${g.id}`); await load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const plan = async (g) => {
    try {
      const r = await api.post(`/advisory/goals/${g.id}/plan`);
      toast.success(r.data.updated ? "What-if item updated in the forecast" : "Added to the forecast as a what-if item");
      onPlanned(r.data.item);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const edit = (g) => {
    setEditId(g.id);
    setForm({ type: g.type, name: g.name, target_amount: g.target_amount || "", target_month: g.target_month || "", debt_id: g.debt_id || "" });
  };

  return (
    <Card testid={testid} className="mt-3">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><Target size={16} /><div className="font-display font-bold text-lg">{title}</div></div>
        {!form && (
          <button onClick={() => { setEditId(null); setForm(EMPTY); }} className="text-xs px-3 py-1.5 text-white bg-[var(--ll-ink)] inline-flex items-center gap-1 hover:opacity-90" data-testid="goal-new-btn">
            <Plus size={12} /> New goal
          </button>
        )}
      </div>
      {form && (
        <div className="border p-3 mb-3 flex flex-wrap items-end gap-2" style={bc} data-testid="goal-form">
          {!editId && (
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp} style={bc} data-testid="goal-type-select">
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          {form.type === "debt_paydown" && !editId && (
            <select value={form.debt_id} onChange={(e) => setForm({ ...form, debt_id: e.target.value })} className={inp} style={bc} data-testid="goal-debt-select">
              <option value="">Choose debt…</option>
              {data.debts.map((d) => <option key={d.id} value={d.id}>{d.name} ({fmtMoney(d.balance)})</option>)}
            </select>
          )}
          {form.type === "cash_buffer" && (
            <input type="number" min="1" placeholder="Target $" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className={`${inp} w-32 font-mono-fin`} style={bc} data-testid="goal-amount-input" />
          )}
          <label className="flex flex-col text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            {form.type === "cash_buffer" ? "By (optional)" : "Target date"}
            <MonthDateInput value={form.target_month} onChange={(v) => setForm((f) => ({ ...f, target_month: v }))} className={`${inp} w-[120px] normal-case tracking-normal font-mono-fin`} style={bc} testid="goal-month-input" />
          </label>
          <input placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inp} flex-1 min-w-[160px]`} style={bc} data-testid="goal-name-input" />
          <button onClick={save} className="text-sm px-3 py-1.5 text-white bg-[var(--ll-ink)] hover:opacity-90" data-testid="goal-save-btn">{editId ? "Update" : "Add goal"}</button>
          <button onClick={() => { setForm(null); setEditId(null); }} className="p-2 border" style={bc} data-testid="goal-cancel-btn"><X size={13} /></button>
        </div>
      )}
      {data.active.length === 0 && !form && (
        <div className="text-sm text-[var(--ll-ink-soft)]" data-testid="goal-empty">Set a cash-buffer or debt-free goal and we'll track progress every time your books update.</div>
      )}
      <ul className="space-y-3">
        {data.active.map((g) => (
          <GoalRow key={g.id} g={g} onEdit={() => edit(g)} onDelete={() => del(g)} onPlan={onPlanned ? () => plan(g) : null} />
        ))}
      </ul>
      {data.completed.length > 0 && (
        <div className="mt-4" data-testid="goal-completed">
          <Eyebrow>Completed</Eyebrow>
          <ul className="mt-2 space-y-1.5">
            {data.completed.map((g) => (
              <li key={g.id} className="flex items-center justify-between text-sm px-3 py-2" style={{ background: "#F5F3FF" }} data-testid={`goal-completed-${g.id}`}>
                <span className="inline-flex items-center gap-2"><Trophy size={14} className="text-[#7C3AED]" /> {g.name}</span>
                <span className="text-xs text-[var(--ll-ink-soft)] inline-flex items-center gap-2">
                  <button onClick={() => edit(g)} className="p-1 hover:text-[var(--ll-ink)]" title="Edit / reopen" data-testid={`goal-edit-${g.id}`}><Pencil size={12} /></button>
                  {g.achieved_at ? `Achieved ${usDate(g.achieved_at)}` : "Achieved"}
                  <button onClick={() => del(g)} className="p-1 hover:text-[var(--ll-error)]" data-testid={`goal-delete-${g.id}`}><Trash2 size={12} /></button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function GoalRow({ g, onEdit, onDelete, onPlan }) {
  const [label, color, bg] = STATE[g.state] || STATE.behind;
  const pct = Math.round((g.progress || 0) * 100);
  const isCash = g.type === "cash_buffer";
  return (
    <li className="border p-3" style={bc} data-testid={`goal-row-${g.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {g.state === "achieved" ? <CheckCircle2 size={15} style={{ color }} /> : <Flag size={14} />}
          <span className="font-medium text-sm truncate">{g.name}</span>
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold" style={{ color, background: bg }} data-testid={`goal-state-${g.id}`}>{label}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-[var(--ll-ink-soft)] mr-2 font-mono-fin">
            {isCash ? `${fmtMoney(g.current)} of ${fmtMoney(g.target)}` : `${fmtMoney(g.current)} left of ${fmtMoney(g.baseline)}`}
            {g.target_month ? ` · by ${monthLabel(g.target_month)}` : ""}
          </span>
          {onPlan && g.state === "behind" && g.extra_needed > 0 && (
            <button onClick={onPlan} className="text-[11px] px-2 py-1 border hover:bg-[var(--ll-mint)] inline-flex items-center gap-1" style={bc} data-testid={`goal-plan-${g.id}`}>
              <LineChart size={12} /> Plan this in forecast
            </button>
          )}
          <button onClick={onEdit} className="p-1.5 border hover:bg-[var(--ll-mint)]" style={bc} data-testid={`goal-edit-${g.id}`}><Pencil size={12} /></button>
          <button onClick={onDelete} className="p-1.5 border hover:bg-[#FEE2E2]" style={bc} data-testid={`goal-delete-${g.id}`}><Trash2 size={12} /></button>
        </div>
      </div>
      <div className="h-2 mt-2.5 bg-[var(--ll-mint)] overflow-hidden" data-testid={`goal-progress-${g.id}`}>
        <div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="flex justify-between gap-3 mt-1.5 text-xs">
        <span className="text-[var(--ll-ink-soft)]" data-testid={`goal-tip-${g.id}`}>{g.tip}</span>
        <span className="font-mono-fin shrink-0">{pct}%</span>
      </div>
      {g.created_by_name && (
        <div className="text-[10px] text-[var(--ll-ink-muted)] mt-1" data-testid={`goal-creator-${g.id}`}>
          Set by {g.created_by_name}{g.created_by_role === "client" ? " (client)" : " (advisor)"}
        </div>
      )}
    </li>
  );
}
